/**
 * El proceso operativo como datos (ADR 0005, doc. 04 §4).
 * El motor interpreta esta definición; no conoce «autos» ni «elevadores», solo unidades, etapas,
 * estaciones y personas. El vocabulario de posventa vive en los datos.
 */

import type { Distribucion } from '../aleatoriedad/distribuciones';
import type { Parametro, OrigenDato } from './parametros';

/** Las cinco categorías del prompt §6.1: todo segundo de una unidad cae en exactamente una. */
export type Categoria =
  'valor-agregado' | 'administrativa' | 'traslado' | 'espera' | 'espera-cliente';

export const CATEGORIAS: readonly Categoria[] = [
  'valor-agregado',
  'administrativa',
  'traslado',
  'espera',
  'espera-cliente',
];

/** Tipos de estación que produce el layout (rol operativo de un elemento del catálogo). */
export type TipoEstacion =
  'bahia' | 'recepcion' | 'patio' | 'lavado' | 'entrega' | 'ingreso' | 'salida';

/**
 * Tipos de bahía que se simulan (el elevador «inactivo» no se simula). Cada servicio indica en
 * cuáles se puede atender (paso 1.2; antes lo fijaba el catálogo de construcción).
 */
export type TipoBahiaActiva = 'mixto' | 'fast-lane' | 'mayor';

export const TIPOS_BAHIA_ACTIVA: readonly TipoBahiaActiva[] = ['mixto', 'fast-lane', 'mayor'];

export type Rol = 'asesor' | 'tecnico';

export const ROLES: readonly Rol[] = ['asesor', 'tecnico'];

/** Hitos del negocio que marca una etapa. */
export type Hito = 'apertura-ot' | 'trabajo-terminado' | 'listo' | 'fin';

/** Mini-lenguaje cerrado de condiciones, sin código ejecutable (doc. 04 §4.2). */
export type Condicion =
  | { tipoUnidad: string }
  | { atributo: string; igual: boolean }
  | { probabilidad: number }
  | { ubicacionActual: TipoEstacion }
  | { y: Condicion[] }
  | { o: Condicion[] }
  | { no: Condicion };

export interface RequisitoPersona {
  rol: Rol;
  /**
   * Hasta cuándo se retiene la persona: al terminar esta etapa (`fin-de-etapa`, por defecto) o al
   * terminar una etapa posterior (su id), como el técnico que hace también el control de calidad.
   */
  retenerHasta?: string;
  /** Usa una persona de este rol que la unidad ya retiene (no adquiere otra). */
  deLaUnidad?: boolean;
}

/** Interrupción de una actividad (por ejemplo, la espera de repuesto a mitad del servicio). */
export interface Interrupcion {
  si: Condicion;
  /** Fracción de la actividad (0–1) en que ocurre. */
  fraccion: Distribucion;
  /** Etapa de espera externa que se intercala (no se alcanza por transiciones). */
  etapa: string;
}

export interface DuracionEtapa {
  /** Distribución en minutos por tipo de unidad; la clave «*» vale para todos. */
  porTipo: Record<string, Distribucion>;
  origen: OrigenDato;
  nota?: string;
  /** Tiempos supuestos que había antes de editarlos («volver al supuesto»). */
  supuesto?: Record<string, Distribucion>;
}

export type TipoEtapa = 'actividad' | 'espera-externa' | 'salida';

export interface Etapa {
  id: string;
  nombre: string;
  tipo: TipoEtapa;
  categoria: Categoria;
  /** Obligatoria para los tipos de `aplicaA` (o para todos si se omite). */
  obligatoria: boolean;
  aplicaA?: string[];
  /** Tipo de estación donde ocurre; `actual` = donde ya está la unidad. */
  lugar: TipoEstacion | 'actual';
  /**
   * Dónde espera la unidad si la etapa no puede empezar: `actual` (retiene y bloquea su
   * estación) o `patio` (estación de amortiguación; si no hay espacio, `actual`).
   */
  lugarDeEspera?: 'actual' | 'patio';
  personas: RequisitoPersona[];
  duracion?: DuracionEtapa;
  /** Solo espera externa: la duración corre en horas hábiles dentro de esta ventana diaria. */
  ventanaHabil?: { inicio: string; fin: string };
  interrupcion?: Interrupcion;
  hitoAlIniciar?: Hito;
  hitoAlTerminar?: Hito;
}

export interface Transicion {
  de: string;
  a: string;
  si?: Condicion;
  /** Ruta por defecto si ninguna condición anterior se cumple. */
  sino?: boolean;
  /**
   * Máximo de veces que una OT toma esta ruta (retrabajo con límite, paso 1.2). Agotado, la ruta ya
   * no se toma y se evalúan las siguientes. La ruta por defecto no lleva límite.
   */
  maximo?: number;
}

export interface TipoDeUnidad {
  id: string;
  nombre: string;
  /** Plazo de la promesa en minutos hábiles desde la apertura de la OT (tipo `habiles`). */
  promesaMin: Parametro<number>;
  /**
   * Cómo se promete (paso 1.2): `habiles` (por defecto), un plazo en horas hábiles desde la
   * apertura de la OT; `hoy`, hoy a una hora fija (si la OT se abre después de esa hora, al día
   * siguiente a la misma hora); `manana`, al día siguiente a una hora fija.
   */
  promesaTipo?: 'habiles' | 'hoy' | 'manana';
  /** Hora fija de la promesa (hh:mm) para `hoy` y `manana`. */
  promesaHora?: Parametro<string>;
  /** Horas estándar facturables del servicio. */
  horasVendidas: Parametro<number>;
  /** Tipos de bahía (elevador) donde se puede atender; un tipo especializado se prefiere. */
  bahias: TipoBahiaActiva[];
}

export interface AtributoDeUnidad {
  id: string;
  nombre: string;
  /** Probabilidad (0–1) por tipo de unidad; los tipos omitidos tienen 0. */
  probabilidad: Parametro<Record<string, number>>;
}

export interface DefinicionDeProceso {
  id: string;
  nombre: string;
  version: number;
  tiposDeUnidad: TipoDeUnidad[];
  atributos: AtributoDeUnidad[];
  etapaInicial: string;
  /** Orden declarado = orden aguas arriba → aguas abajo (la fase C lo recorre al revés). */
  etapas: Etapa[];
  transiciones: Transicion[];
}

/** Distribución de una etapa para un tipo de unidad, o `undefined` si no está definida. */
export function distribucionDe(etapa: Etapa, tipoUnidad: string): Distribucion | undefined {
  return etapa.duracion?.porTipo[tipoUnidad] ?? etapa.duracion?.porTipo['*'];
}

/** ¿La etapa es obligatoria para este tipo de unidad? */
export function esObligatoriaPara(etapa: Etapa, tipoUnidad: string): boolean {
  return etapa.obligatoria && (etapa.aplicaA === undefined || etapa.aplicaA.includes(tipoUnidad));
}
